
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatDialog } from '@angular/material/dialog';

import { AzavistaApiService, EventActivityDisplay, IEvent, IEventActivityGlobalSettings, IUser } from '@azavista/servicelib';
import { IIdWithName, IField, IInputFieldOptions, IObjectWithId, IValueChangedEventArgs, IValueTranslations, FieldComponentType } from '@azavista/components/shared';
import { IDependenciesPopupComponentData } from 'src/app/website-builder/dependencies-popup/interfaces';
import { DependenciesPopupComponent } from 'src/app/website-builder/dependencies-popup/dependencies-popup.component';
import { ITranslation } from 'src/app/website-builder/interfaces';
import { SharedService } from '../shared.service';
import { IObjectEnumFieldWithValue } from '../shared.interfaces';
import { TranslationKey } from '../enums';
import { TRANSLATION_MAP } from '../const';

@Component({
    selector: 'azavista3-activity-settings-fields',
    templateUrl: 'activity-settings-fields.component.html',
    standalone: false
})
export class ActivitySettingsFieldsComponent implements OnInit {
    @Input() event: IEvent;
    @Input() eventActivitySettings: IEventActivityGlobalSettings;

    @Output() settingsChanged = new EventEmitter<void>();

    fields: IGenericActivitySettingsFields;

    translations = TRANSLATION_MAP;

    private user: IUser;

    constructor(
        private apiSvc: AzavistaApiService, private sharedSvc: SharedService,
        private readonly matDialog: MatDialog
    ) {
    }

    async ngOnInit(): Promise<void> {
        this.user = this.apiSvc.getCurrentUser();
        this.fields = await this.createFields(this.event.id, this.eventActivitySettings);
    }

    onDependencyValueChanged(args: IValueChangedEventArgs): void {
        const value = (args?.value as IIdWithName)?.id || null;
        this.setSetting({ dependency_id: value });
    }

    onDisplayValueChanged(args: IValueChangedEventArgs): void {
        const value = args.value as EventActivityDisplay[];
        this.setSetting({ display: value });
    }

    // onRegistrationValueChanged(args: IValueChangedEventArgs): void {
    //     const value = args.value as boolean;
    //     this.setSetting({ registration: value });
    // }

    onRegistrationSlideValueChanged(args: MatSlideToggleChange): void {
        if (!args.checked) {
            this.setSetting({ registration: args.checked, waitinglist: false });
        } else {
            this.setSetting({ registration: args.checked });
        }
    }

    onCapacityValueChanged(args: IValueChangedEventArgs): void {
        const value = args.value as string;
        const integerValue = parseInt(value);
        if (isNaN(integerValue)) {
            this.setSetting({ capacity: null, waitinglist: false });
        } else {
            if (integerValue >= 0) {
                this.setSetting({ capacity: integerValue });
            } else {
                this.setSetting({ capacity: null, waitinglist: false });
            }
        }
    }

    // onWaitingListValueChanged(args: IValueChangedEventArgs): void {
    //     const value = args.value as boolean;
    //     this.setSetting({ waitinglist: value });
    // }

    onWaitingListSlideValueChanged(args: MatSlideToggleChange): void {
        this.setSetting({ waitinglist: args.checked });
    }

    onPromotionEmailValueChanged(args: IValueChangedEventArgs): void {
        const value = (args.value as IIdWithName).id;
        this.setSetting({ promotion_email_id: value });
    }

    async showDependencies(): Promise<void> {
        const componentData: IDependenciesPopupComponentData = {
            event: this.event,
        };
        this.matDialog.open(DependenciesPopupComponent, {
            data: componentData
        }).afterClosed().subscribe({
            next: async () => {
                this.fields.dependency.value = null;
                this.sharedSvc.executeOnNextLoop(async () => {
                    const eventDependencies = await this.apiSvc.getAllDependencies(this.event.id);
                    if (!eventDependencies.find(x => x.id === this.eventActivitySettings.dependency_id)) {
                        this.eventActivitySettings.dependency_id = null;
                    }
                    this.fields.dependency.value = this.sharedSvc.createDefaultObjectEnumInputFieldValue(
                        eventDependencies as IObjectWithId[], this.eventActivitySettings.dependency_id
                    );
                });
            }
        });
    }

    setSetting(partialSettings: Partial<IEventActivityGlobalSettings>): void {
        Object.assign(this.eventActivitySettings, partialSettings);
        this.settingsChanged.next();
    }

    private async createFields(eventId: string, globalActivitySettings: IEventActivityGlobalSettings): Promise<IGenericActivitySettingsFields> {
        const eventDependencies = await this.apiSvc.getAllDependencies(eventId);
        const eventEmailCampaigns = await this.apiSvc.getAllEventEmailCampaigns(eventId);
        const activityDisplayItems: IIdWithName[] = [
            { id: EventActivityDisplay.website, name: this.sharedSvc.translate(TranslationKey.website) },
            { id: EventActivityDisplay.engagementApp, name: this.sharedSvc.translate(TranslationKey.engagementApp) }
        ];
        const result: IGenericActivitySettingsFields = {
            dependency: {
                field: this.sharedSvc.createDefaultObjectEnumField('dependency_id'),
                value: this.sharedSvc.createDefaultObjectEnumInputFieldValue(
                    eventDependencies as IObjectWithId[], globalActivitySettings.dependency_id
                ),
            },
            capacity: this.createCapacityField('capacity'),
            display: this.createEnumField('display', activityDisplayItems.map(x => x.id)),
            displayOptions: { translations: this.createEnumFieldTranslations(this.user.language, activityDisplayItems) },
            promotionEmail: {
                field: this.sharedSvc.createDefaultObjectEnumField('promotion_email_id'),
                value: this.sharedSvc.createDefaultObjectEnumInputFieldValue(
                    eventEmailCampaigns as IObjectWithId[], globalActivitySettings.promotion_email_id
                )
            },
            // registration: this.createBooleanField('registration'),
            // waitinglist: this.createBooleanField('waitinglist')
        };
        return result;
    }

    private createCapacityField(id: string): IField {
        const result = {
            id: id, name: id, schema: { type: 'integer' }, editable: true, type: 'integer',
        } as IField;
        return result;
    }

    private createEnumField(id: string, items: string[]): IField {
        const result = {
            id: id, name: id, editable: true,
            schema: { type: 'array', items: { type: 'string', enum: items } }
        } as IField;
        return result;
    }

    private createEnumFieldTranslations(language: string, items: IIdWithName[]): IValueTranslations {
        const translationsObject: ITranslation = {};
        for (const item of items) {
            translationsObject[item.id] = item.name;
        }
        const result: IValueTranslations = {
            [language]: translationsObject
        };
        return result;
    }
}

interface IGenericActivitySettingsFields {
    dependency: IObjectEnumFieldWithValue;
    display: IField;
    displayOptions: IInputFieldOptions;
    // registration: IField;
    capacity: IField;
    // waitinglist: IField;
    promotionEmail: IObjectEnumFieldWithValue;
}
